import { TFunction } from "i18next";

import {
  BulletinEntity,
  BulletinFormValues,
  EntityType,
  ITEM_INVOLVEMENTS,
  PERSON_INVOLVEMENTS,
  VEHICLE_INVOLVEMENTS,
  VehicleEntity,
} from "./types";

/**
 * The entity rules, as a pure module.
 *
 * Same reasoning as `model/incidentTimes.ts` and `model/contactRows.ts`: these are rules phase 3's
 * schema has to agree with, and they are far easier to pin down — and to test — as functions than
 * through rendered fields.
 *
 * Errors are returned as **codes**, not messages. The editor translates them, and phase 3's Zod
 * schema can call the same functions rather than restating the rules in a second place.
 */

/** Involvement options per type. The prototype's lists, already the source for the model's unions. */
export const INVOLVEMENT_OPTIONS: Record<EntityType, readonly string[]> = {
  person: PERSON_INVOLVEMENTS,
  vehicle: VEHICLE_INVOLVEMENTS,
  item: ITEM_INVOLVEMENTS,
};

/**
 * Bulletin types where a person's first name, last name and date of birth are required rather than
 * optional. The design's list, verbatim: these are the bulletins whose whole point is identifying
 * somebody, and the board card has nothing to show without them.
 */
export const IDENTITY_REQUIRED_BULLETIN_TYPES: ReadonlySet<string> = new Set([
  "arrests_made",
  "bail_compliance",
  "missing_person",
  "warrant",
  "warrant_request",
  "wellness_check",
]);

export const requiresIdentity = (bulletinType: string | null): boolean =>
  bulletinType !== null && IDENTITY_REQUIRED_BULLETIN_TYPES.has(bulletinType);

/**
 * Whether the involvement dropdown has to be answered. True for a person and a vehicle, false for
 * an item.
 *
 * An item is the catch-all — the placeholder copy is "Backpack, firearm, red hoodie, business name,
 * location clue" — and most of those have no role in the case to state. Asking anyway produces
 * either a wrong answer or a typed-through one; the legacy tags migrated into this type never had
 * a role recorded at all, which is what surfaced it (issue #941).
 *
 * Exported so `EntityEditor` marks the field from the same rule that validates it, rather than
 * restating it as a second condition that can drift.
 */
export const requiresInvolvement = (type: EntityType): boolean =>
  type !== "item";

/**
 * Accepts what an officer actually types and returns an ISO calendar date.
 *
 * `""` for blank, `null` for something that cannot be read as a date, `YYYY-MM-DD` otherwise. The
 * ambiguous `03-04-1990` is read day-first only when the first number cannot be a month, which is
 * the prototype's rule.
 *
 * The upper bound is the current year rather than the prototype's hardcoded 2026 — a literal would
 * start rejecting valid input the following January.
 */
export const normalizeDateOfBirth = (input: string | null): string | null => {
  const text = (input ?? "").trim();
  if (!text) return "";

  let year: number;
  let month: number;
  let day: number;
  let match: RegExpMatchArray | null;

  if ((match = text.match(/^(\d{4})[-/. ](\d{1,2})[-/. ](\d{1,2})$/))) {
    [year, month, day] = [+match[1], +match[2], +match[3]];
  } else if ((match = text.match(/^(\d{1,2})[-/. ](\d{1,2})[-/. ](\d{4})$/))) {
    if (+match[1] > 12) {
      [day, month] = [+match[1], +match[2]];
    } else {
      [month, day] = [+match[1], +match[2]];
    }
    year = +match[3];
  } else if ((match = text.match(/^(\d{4})(\d{2})(\d{2})$/))) {
    [year, month, day] = [+match[1], +match[2], +match[3]];
  } else {
    return null;
  }

  const thisYear = new Date().getFullYear();
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  if (year < 1900 || year > thisYear) return null;

  // A fixed bound of 31 admits 31 February, and `Date` normalises rather than refusing it — so
  // the only reliable test is whether the parts survive the round trip. This is what keeps a
  // typo out of `date_of_birth`, where it would be stored as a real date three days later.
  const asDate = new Date(Date.UTC(year, month - 1, day));
  if (asDate.getUTCMonth() + 1 !== month || asDate.getUTCDate() !== day) {
    return null;
  }

  const pad = (value: number) => String(value).padStart(2, "0");
  return `${year}-${pad(month)}-${pad(day)}`;
};

/**
 * The two answers that are not a make or a model. They mirror the backend's own sentinels — an
 * officer often cannot tell what they were looking at, and "not filled in yet" has to stay
 * distinguishable from "looked, and could not tell".
 */
export const VEHICLE_UNKNOWN = "unknown";
export const VEHICLE_OTHER = "other";

const isVehicleSentinel = (value: string) =>
  value === VEHICLE_UNKNOWN || value === VEHICLE_OTHER;

/**
 * Writes a make and brings the model with it.
 *
 * Two rules, both the design's, and both here rather than in the field for the reason
 * `model/incidentTimes.ts` gives: phase 3's schema has to agree with them.
 *
 * 1. **Changing the make clears the model.** Models are fetched per make, so a Civic cannot survive
 *    into a Toyota. Same reasoning as decision 21's police-service/operational-area clear.
 * 2. **A sentinel make forces the model to `other`.** If the brand is unknown or unlisted there is
 *    no model list to offer, so the only thing left is free text.
 */
export const applyVehicleMake = (
  entity: VehicleEntity,
  make: string,
): VehicleEntity => {
  if (make === entity.make) return entity;

  return {
    ...entity,
    make,
    // Free text is only meaningful behind `other`; anything else must not leave a stale string
    // that no input on screen shows.
    makeOther: make === VEHICLE_OTHER ? entity.makeOther : "",
    model: isVehicleSentinel(make) ? VEHICLE_OTHER : "",
    modelOther: "",
  };
};

/** As `applyVehicleMake`, for the model — it only has to keep its own free text honest. */
export const applyVehicleModel = (
  entity: VehicleEntity,
  model: string,
): VehicleEntity => ({
  ...entity,
  model,
  modelOther: model === VEHICLE_OTHER ? entity.modelOther : "",
});

/**
 * Colour and vehicle type, the design's lists verbatim, as **keys**.
 *
 * They live here rather than behind an endpoint for the reason a ten-value list always does: there
 * is nothing to administer and nothing to translate server-side — `entities.colour_<key>` is what
 * renders them, in either language. `other` closes both lists, as it does make and model.
 */
export const VEHICLE_COLOURS = [
  "white",
  "black",
  "silver",
  "gray",
  "red",
  "blue",
  "green",
  "brown",
  "gold",
  VEHICLE_OTHER,
] as const;

export const VEHICLE_TYPES = [
  "sedan",
  "suv",
  "pickup_truck",
  "van",
  "motorcycle",
  "atv",
  "snowmobile",
  "trailer",
  "motor_home",
  VEHICLE_OTHER,
] as const;

/** As `applyVehicleModel`: the free text behind `other` must not outlive the choice. */
export const applyVehicleColour = (
  entity: VehicleEntity,
  colour: string,
): VehicleEntity => ({
  ...entity,
  colour,
  colourOther: colour === VEHICLE_OTHER ? entity.colourOther : "",
});

/** As `applyVehicleColour`, for the vehicle type. */
export const applyVehicleType = (
  entity: VehicleEntity,
  vehicleType: string,
): VehicleEntity => ({
  ...entity,
  vehicleType,
  vehicleTypeOther:
    vehicleType === VEHICLE_OTHER ? entity.vehicleTypeOther : "",
});

/** The model field offers nothing but free text once the make is a sentinel. */
export const vehicleModelIsForcedOther = (entity: VehicleEntity): boolean =>
  isVehicleSentinel(entity.make);

/** What a reader should see: the free text behind `other`, and nothing for `unknown`. */
export const resolveVehicleValue = (value: string, other: string): string => {
  if (value === VEHICLE_OTHER) return other;
  return value === VEHICLE_UNKNOWN ? "" : value;
};

/**
 * As `resolveVehicleValue`, for the two lists that store a key rather than a name — the key has to
 * go through `t` before anybody reads it.
 */
const resolveVehicleChoice = (
  value: string,
  other: string,
  prefix: "colour" | "vehicle_type",
  t: TFunction,
): string => {
  if (value === VEHICLE_OTHER) return other;
  return value ? t(`entities.${prefix}_${value}`) : "";
};

/**
 * `Person #2` counts people only — numbering is per type, derived at render time and never stored,
 * so deleting one renumbers the rest.
 */
export const numberWithinType = (
  entities: BulletinEntity[],
  entity: BulletinEntity,
): number =>
  entities
    .filter((candidate) => candidate.type === entity.type)
    // By `id`, not `indexOf`: reference equality makes a copied row — a patched object, a
    // deferred snapshot — return -1 and label the card `#0`.
    .findIndex((candidate) => candidate.id === entity.id) + 1;

/**
 * The one line shown beside the chip on a collapsed card.
 *
 * Takes `t` for the same reason `entityChipLabel` does. Most of the line is what the officer typed
 * and needs no translation; colour and vehicle type are picked from a list and stored as keys, so
 * they do.
 */
export const entitySummaryLine = (
  entity: BulletinEntity,
  t: TFunction,
): string => {
  switch (entity.type) {
    case "person":
      return [entity.firstName, entity.lastName, entity.dateOfBirth]
        .filter((part) => part && String(part).trim())
        .join(" ");
    case "vehicle":
      return [
        entity.licencePlate,
        resolveVehicleValue(entity.make, entity.makeOther),
        resolveVehicleValue(entity.model, entity.modelOther),
        resolveVehicleChoice(entity.colour, entity.colourOther, "colour", t),
        resolveVehicleChoice(
          entity.vehicleType,
          entity.vehicleTypeOther,
          "vehicle_type",
          t,
        ),
      ]
        .filter((part) => part.trim())
        .join(" ");
    case "item":
      return [entity.itemName, entity.additionalDescription]
        .filter((part) => part.trim())
        .join(" — ");
  }
};

/**
 * True once the officer has put anything into a row. Two callers, both about the same thing —
 * a row nobody has touched is not part of the bulletin: `EntitiesSection` drops it when the card
 * collapses, and `withSavableEntities` keeps it out of the payload.
 */
export const entityDraftHasContent = (entity: BulletinEntity): boolean => {
  const shared =
    entity.involvement !== null ||
    entity.otherInvolvement.trim() !== "" ||
    entity.additionalDescription.trim() !== "";
  if (shared) return true;

  switch (entity.type) {
    case "person":
      return [entity.firstName, entity.lastName, entity.dateOfBirth ?? ""].some(
        (value) => value.trim() !== "",
      );
    case "vehicle":
      return [
        entity.make,
        entity.makeOther,
        entity.model,
        entity.modelOther,
        entity.colour,
        entity.colourOther,
        entity.vehicleType,
        entity.vehicleTypeOther,
        entity.licencePlate,
        entity.jurisdiction,
      ].some((value) => value.trim() !== "");
    case "item":
      return entity.itemName.trim() !== "";
  }
};

/**
 * The document as persistence should see it: a row the officer has not put anything into yet is not
 * part of the bulletin.
 *
 * Clicking Add puts a blank row in the form immediately — that is what gives it an id for media to
 * point at — and `EntitiesSection` deletes it again when it collapses untouched. That cleanup is
 * not enough on its own, and both gaps were reproduced before this existed: blur fires before
 * click, so Add-then-Done autosaved the blank and then deleted it (an INSERT and a DELETE against
 * the real endpoint); and a row simply left open was saved and stayed saved.
 *
 * So the blank is UI scaffolding, exactly like the trailing blank `normalizeContactRows` keeps, and
 * like that one it is filtered on the way out rather than prevented on the way in.
 *
 * `autoAdded` rows survive: a seeded placeholder is empty by definition and is still meant to be
 * part of the bulletin.
 */
export const withSavableEntities = (
  values: BulletinFormValues,
): BulletinFormValues => ({
  ...values,
  entities: values.entities.filter(
    (entity) => entityDraftHasContent(entity) || entity.autoAdded,
  ),
});

/** Field name → error code. The editor maps codes to copy; an empty object means the draft saves. */
export type EntityErrorCode =
  | "required"
  | "required_for_type"
  | "specify_involvement"
  | "invalid_date";

export type EntityErrors = Partial<
  Record<
    | "involvement"
    | "otherInvolvement"
    | "firstName"
    | "lastName"
    | "dateOfBirth"
    | "itemName",
    EntityErrorCode
  >
>;

/**
 * The design's per-entity rules, as one function.
 *
 * It lives here rather than in `validation/` so the rules sit beside the rest of the entity model,
 * and it has two callers, both of which go through it rather than restating the rules:
 *
 * - `validation/schemas.ts` calls it from the `entities` `superRefine`, which is what surfaces
 *   these errors on a finalize attempt.
 * - `sections/EntitiesSection.tsx` calls it directly, to mark the row the officer is editing.
 */
export const validateEntityDraft = (
  entity: BulletinEntity,
  bulletinType: string | null,
): EntityErrors => {
  const errors: EntityErrors = {};

  if (!entity.involvement && requiresInvolvement(entity.type)) {
    errors.involvement = "required";
  }
  // Still required wherever it shows. Picking `other` is a claim that there IS a role and it is
  // not in the list, so it has to say which. "No role" is said by leaving the dropdown empty —
  // which is why an item needs no reserved word standing in for it.
  if (entity.involvement === "other" && !entity.otherInvolvement.trim()) {
    errors.otherInvolvement = "specify_involvement";
  }

  if (entity.type === "person") {
    if (requiresIdentity(bulletinType)) {
      if (!entity.firstName.trim()) errors.firstName = "required_for_type";
      if (!entity.lastName.trim()) errors.lastName = "required_for_type";
      if (!(entity.dateOfBirth ?? "").trim()) {
        errors.dateOfBirth = "required_for_type";
      }
    }
    // A date that was typed has to be readable whether or not it was required.
    if (
      (entity.dateOfBirth ?? "").trim() &&
      normalizeDateOfBirth(entity.dateOfBirth) === null
    ) {
      errors.dateOfBirth = "invalid_date";
    }
  }

  if (entity.type === "item" && !entity.itemName.trim()) {
    errors.itemName = "required";
  }

  return errors;
};

/**
 * The entity as the payload wants it: the typed date becomes ISO, everything else passes through.
 *
 * For `model/adapters.ts`, the last of phase 2's gate. The field stores what the officer typed —
 * normalising per keystroke would fight the cursor — so this is where `PersonEntity.dateOfBirth`'s
 * "ISO calendar date" contract is actually met.
 */
export const normalizeEntityForSave = (
  entity: BulletinEntity,
): BulletinEntity =>
  entity.type === "person"
    ? {
        ...entity,
        dateOfBirth: normalizeDateOfBirth(entity.dateOfBirth) || null,
      }
    : entity;
